import { useReportDateAccess, useReportDateState } from "../hooks/useReportDateAccess";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import {
  FaChartLine,
  FaClock,
  FaFilter,
  FaLayerGroup,
  FaSearch,
  FaShieldAlt,
  FaStore,
  FaSyncAlt,
  FaUsers,
  FaPrint,
} from "react-icons/fa";
import { getStoredUser } from "../utils/impersonation";
import { getAccountLinkContext, isPrimaryLinkedRestaurantSession } from "../utils/accountLinks";
import { canAccessFeature } from "../utils/rbac";
import { formatMoney, getCurrencySymbol, readStoredCurrencyContext } from "../utils/currency";
import ReportPageHeader from "../components/ReportPageHeader";
import { downloadExcel, printThermal, printThermalViaUsb} from "../utils/exportHelpers";
import { API_BASE_URL } from "../config/constants";
import { isFullOfflineModeEnabled } from "../utils/fullOfflineMode";
import { getOfflineRestaurantsOverview } from "../offline/financeStore";

const API_BASE = process.env.REACT_APP_API_BASE || API_BASE_URL;

const EMPTY_DATA = {
  summary: {
    matched_restaurants: 0,
    active_restaurants: 0,
    total_sales: 0,
    total_orders: 0,
    avg_order_value: 0,
  },
  top_restaurants: [],
  rows: [],
  pagination: {
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0,
  },
};

const toYmd = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  const pad = (part) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const today = () => toYmd(new Date());

const daysAgo = (count) => {
  const date = new Date();
  date.setDate(date.getDate() - count);
  return toYmd(date);
};

const buildDefaultFilters = () => ({
  fromDate: daysAgo(30),
  toDate: today(),
  query: "",
  planStatus: "all",
  webAccess: "all",
});

const SORT_LABELS = {
  restaurant_name: "Restaurant Name",
  total_orders: "Total Orders",
  total_sales: "Total Sales",
  avg_order_value: "Average Bill Value",
  last_order_at: "Last Order Time",
};

const SORT_DIRECTION_LABELS = {
  asc: "Low to High",
  desc: "High to Low",
};

const PLAN_STATUS_LABELS = {
  all: "All Plans",
  trial: "Trial Plan",
  paid: "Paid Plan",
  expired: "Expired Plan",
};

const WEB_ACCESS_LABELS = {
  all: "All Accounts",
  allowed: "Web Access On",
  blocked: "Web Access Off",
};

const PACKAGE_LABELS = {
  basic: "Basic",
  lite: "Lite",
  starter: "Starter",
  professional: "Professional",
};

const getLabel = (value, labels, fallback = "-") => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return fallback;
  return labels[raw] || raw.charAt(0).toUpperCase() + raw.slice(1).replace(/_/g, " ");
};

const formatDateTime = (value) => {
  if (!value) return "No orders in range";
  try {
    return new Date(value).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

const RestaurantsOverviewPage = () => {
  const fullOfflineMode = isFullOfflineModeEnabled();
  const { todayOnly } = useReportDateAccess();
  const viewer = getStoredUser();
  const linkedContext = getAccountLinkContext();
  const isLinkedPrimaryViewer = isPrimaryLinkedRestaurantSession();
  const canView = canAccessFeature("impersonation", viewer) || isLinkedPrimaryViewer;
  const [draftFilters, setDraftFilters] = useReportDateState(buildDefaultFilters, "filters");
  const [appliedFilters, setAppliedFilters] = useReportDateState(buildDefaultFilters, "filters");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [sortBy, setSortBy] = useState("total_sales");
  const [sortDir, setSortDir] = useState("desc");
  const [refreshToken, setRefreshToken] = useState(0);
  const [data, setData] = useState(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdatedAt, setLastUpdatedAt] = useState("");
  const hasLoadedRef = useRef(false);
  const reportRef = useRef(null);

  const currencyFormatter = useMemo(() => {
    const context = readStoredCurrencyContext();
    return {
      format: (value) => formatMoney(value, context, { maximumFractionDigits: 0 }),
    };
  }, []);
  const currencySymbol = useMemo(
    () => getCurrencySymbol(readStoredCurrencyContext()),
    []
  );

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }),
    []
  );

  useEffect(() => {
      if (!canView) {
        setLoading(false);
        return undefined;
    }

    let active = true;

    const loadReport = async () => {
      if (!hasLoadedRef.current) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      setError("");

      try {
        const requestPayload = {
          restaurantid: isLinkedPrimaryViewer
            ? Number(linkedContext?.primary_restaurant_id || viewer?.id || 0)
            : undefined,
          fromdate: appliedFilters.fromDate,
          todate: appliedFilters.toDate,
          query: appliedFilters.query.trim(),
          plan_status: appliedFilters.planStatus,
          web_access: appliedFilters.webAccess,
          page,
          per_page: perPage,
          sort_by: sortBy,
          sort_dir: sortDir,
        };
        let json;
        if (fullOfflineMode) {
          const accounts = Array.isArray(linkedContext?.accounts) && linkedContext.accounts.length
            ? linkedContext.accounts
            : viewer ? [viewer] : [];
          json = await getOfflineRestaurantsOverview({
            ...requestPayload,
            accounts,
            scopeAll: !isLinkedPrimaryViewer,
          });
        } else {
          const response = await fetch(`${API_BASE}/report-v2/restaurants-overview`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestPayload),
          });
          json = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(json?.message || "Unable to load restaurant overview.");
          }
        }

        if (!active) return;

        setData({
          summary: {
            matched_restaurants: Number(json?.summary?.matched_restaurants || 0),
            active_restaurants: Number(json?.summary?.active_restaurants || 0),
            total_sales: Number(json?.summary?.total_sales || 0),
            total_orders: Number(json?.summary?.total_orders || 0),
            avg_order_value: Number(json?.summary?.avg_order_value || 0),
          },
          top_restaurants: Array.isArray(json?.top_restaurants) ? json.top_restaurants : [],
          rows: Array.isArray(json?.rows) ? json.rows : [],
          pagination: {
            page: Number(json?.pagination?.page || page),
            per_page: Number(json?.pagination?.per_page || perPage),
            total: Number(json?.pagination?.total || 0),
            total_pages: Number(json?.pagination?.total_pages || 0),
          },
        });
        hasLoadedRef.current = true;
        setLastUpdatedAt(new Date().toISOString());
      } catch (fetchError) {
        if (!active) return;
        setError(fetchError?.message || "Unable to load restaurant overview.");
        setData(EMPTY_DATA);
        hasLoadedRef.current = true;
      } finally {
        if (!active) return;
        setLoading(false);
        setRefreshing(false);
      }
    };

    loadReport();

    return () => {
      active = false;
    };
  }, [
    appliedFilters,
    canView,
    fullOfflineMode,
    isLinkedPrimaryViewer,
    linkedContext?.primary_restaurant_id,
    page,
    perPage,
    refreshToken,
    sortBy,
    sortDir,
    viewer?.id,
  ]);

  const totalPages = Number(data?.pagination?.total_pages || 0);

  const summaryCards = [
    {
      label: "Total Restaurants",
      value: numberFormatter.format(data.summary.matched_restaurants || 0),
      accent: "#0f766e",
      icon: <FaStore />,
      helper: "Restaurants in this report",
    },
    {
      label: "Active Restaurants",
      value: numberFormatter.format(data.summary.active_restaurants || 0),
      accent: "var(--app-primary)",
      icon: <FaUsers />,
      helper: "At least one order in range",
    },
    {
      label: "Total Sales",
      value: currencyFormatter.format(data.summary.total_sales || 0),
      accent: "var(--app-primary)",
      icon: <span>{currencySymbol}</span>,
      helper: "Across filtered restaurants",
    },
    {
      label: "Total Orders",
      value: numberFormatter.format(data.summary.total_orders || 0),
      accent: "#7c3aed",
      icon: <FaLayerGroup />,
      helper: "Valid completed orders only",
    },
    {
      label: "Average Bill Value",
      value: currencyFormatter.format(data.summary.avg_order_value || 0),
      accent: "#b45309",
      icon: <FaChartLine />,
      helper: "Total sales / total orders",
    },
  ];

  const onDraftChange = (key, value) => {
    setDraftFilters((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const exportExcel = () => {
    if (!data.rows.length) return;
    const headers = [
      "#",
      "Restaurant",
      "Owner",
      "Mobile",
      "Plan",
      "Web Access",
      "Orders",
      "Sales",
      "Avg Bill Value",
      "Last Order",
    ];
    const exportRows = data.rows.map((row, index) => [
      (page - 1) * perPage + index + 1,
      row.restaurant_name || "-",
      row.owner_name || "-",
      row.mobile_number || "-",
      row.plan_status || "-",
      row.web_access_enabled ? "Enabled" : "Disabled",
      row.total_orders || 0,
      row.total_sales || 0,
      row.avg_order_value || 0,
      row.last_order_at ? new Date(row.last_order_at).toLocaleString("en-IN") : "Never",
    ]);
    downloadExcel("restaurants_overview.xlsx", headers, exportRows);
  };

  const onApplyFilters = (event) => {
    event.preventDefault();
    setPage(1);
    setAppliedFilters({ ...draftFilters });
    setRefreshToken((current) => current + 1);
  };

  const onRefresh = () => {
    setRefreshToken((current) => current + 1);
  };

  const handleSort = (column) => {
    const nextDir =
      sortBy === column ? (sortDir === "asc" ? "desc" : "asc") : column === "restaurant_name" ? "asc" : "desc";
    setSortBy(column);
    setSortDir(nextDir);
    setPage(1);
  };

  if (!canView) {
    return (
      <Page>
        <NoticeCard>
          <NoticeTitle>Restaurants Overview</NoticeTitle>
          <NoticeText>This report is available only for master access.</NoticeText>
        </NoticeCard>
      </Page>
    );
  }

  return (
    <Page ref={reportRef}>
      <ReportPageHeader
        title="Restaurants Overview"
        subtitle="Cross-restaurant analytics, active stores, and performance insights."
        stats={[
          { label: "Total Restaurants", value: numberFormatter.format(data.summary.matched_restaurants || 0) },
          { label: "Active Restaurants", value: numberFormatter.format(data.summary.active_restaurants || 0) },
          { label: "Total Sales", value: currencyFormatter.format(data.summary.total_sales || 0) },
          { label: "Total Orders", value: numberFormatter.format(data.summary.total_orders || 0) },
        ]}
        actions={
          <button
            type="button"
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "1px solid rgba(255,255,255,0.3)",
              color: "#fff",
              padding: "8px 14px",
              borderRadius: "10px",
              fontWeight: "700",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
            onClick={() => setRefreshToken((prev) => prev + 1)}
          >
            <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        }
      />

      <FilterCard as="form" onSubmit={onApplyFilters}>
        <FilterTop>
          <FilterTitle>
            <FaFilter />
            Filters
          </FilterTitle>
          <FilterActions>
            <GhostButton
              type="button"
              onClick={() => {
                const defaults = buildDefaultFilters();
                setDraftFilters(defaults);
                setPage(1);
                setAppliedFilters(defaults);
                setRefreshToken((current) => current + 1);
              }}
            >
              Reset
            </GhostButton>
            <PrimaryButton type="button" onClick={onRefresh} disabled={loading || refreshing}>
              <FaSyncAlt />
              {refreshing ? "Refreshing..." : "Refresh"}
            </PrimaryButton>
          </FilterActions>
        </FilterTop>

        <FilterGrid>
          <Field>
            <label>From Date</label>
            <input
              type="date"
              value={draftFilters.fromDate}
              disabled={todayOnly}
              max={draftFilters.toDate}
              onChange={(event) => onDraftChange("fromDate", event.target.value)}
            />
          </Field>
          <Field>
            <label>To Date</label>
            <input
              type="date"
              value={draftFilters.toDate}
              disabled={todayOnly}
              min={draftFilters.fromDate}
              max={today()}
              onChange={(event) => onDraftChange("toDate", event.target.value)}
            />
          </Field>
          <Field>
            <label>Restaurant Search</label>
            <InputWrap>
              <FaSearch />
              <input
                type="text"
                value={draftFilters.query}
                placeholder="Name, owner, mobile, or restaurant ID"
                onChange={(event) => onDraftChange("query", event.target.value)}
              />
            </InputWrap>
          </Field>
          <Field>
            <label>Plan</label>
            <select
              value={draftFilters.planStatus}
              onChange={(event) => onDraftChange("planStatus", event.target.value)}
            >
              <option value="all">All Plans</option>
              <option value="trial">Trial Plan</option>
              <option value="paid">Paid Plan</option>
              <option value="expired">Expired Plan</option>
            </select>
          </Field>
          <Field>
            <label>Web Access</label>
            <select
              value={draftFilters.webAccess}
              onChange={(event) => onDraftChange("webAccess", event.target.value)}
            >
              <option value="all">All Accounts</option>
              <option value="allowed">Web Access On</option>
              <option value="blocked">Web Access Off</option>
            </select>
          </Field>
          <Field>
            <label>Show Rows</label>
            <select
              value={perPage}
              onChange={(event) => {
                setPerPage(Number(event.target.value) || 20);
                setPage(1);
              }}
            >
              {[10, 20, 50, 100].map((value) => (
                <option key={value} value={value}>
                  {value} rows
                </option>
              ))}
            </select>
          </Field>
        </FilterGrid>

        <FooterRow>
          <AppliedCopy>
            Showing data from <b>{appliedFilters.fromDate}</b> to <b>{appliedFilters.toDate}</b>
          </AppliedCopy>
          <PrimaryButton type="submit" disabled={loading || refreshing}>
            Apply Filters
          </PrimaryButton>
        </FooterRow>
      </FilterCard>

      {error ? (
        <NoticeCard $danger>
          <NoticeTitle>Unable to load overview</NoticeTitle>
          <NoticeText>{error}</NoticeText>
        </NoticeCard>
      ) : null}

      <SummaryGrid>
        {summaryCards.map((item) => (
          <SummaryCard key={item.label}>
            <SummaryIcon $accent={item.accent}>{item.icon}</SummaryIcon>
            <SummaryLabel>{item.label}</SummaryLabel>
            <SummaryValue>{item.value}</SummaryValue>
            <SummaryHelper>{item.helper}</SummaryHelper>
          </SummaryCard>
        ))}
      </SummaryGrid>

      <SplitGrid>
        <Panel>
          <PanelHead>
            <div>
              <PanelTitle>Top Restaurants</PanelTitle>
              <PanelSub>Highest sales performers for the active filters.</PanelSub>
            </div>
            <PanelMeta>{numberFormatter.format(data.top_restaurants.length)} shown</PanelMeta>
          </PanelHead>

          {loading ? (
            <CenterState>Loading top restaurants...</CenterState>
          ) : data.top_restaurants.length === 0 ? (
            <CenterState>No restaurant activity found for this range.</CenterState>
          ) : (
            <TopList>
              {data.top_restaurants.map((restaurant, index) => (
                <TopCard key={`${restaurant.restaurant_id}-${index}`}>
                  <TopRank>#{index + 1}</TopRank>
                  <TopName>{restaurant.restaurant_name}</TopName>
                  <TopMeta>{restaurant.mobile_number || "No mobile"}</TopMeta>
                  <TopStats>
                    <span>{currencyFormatter.format(restaurant.total_sales || 0)}</span>
                    <span>{numberFormatter.format(restaurant.total_orders || 0)} orders</span>
                  </TopStats>
                </TopCard>
              ))}
            </TopList>
          )}
        </Panel>

        <Panel>
          <PanelHead>
            <div>
              <PanelTitle>Report Summary</PanelTitle>
              <PanelSub>Quick view of the filters used for this report.</PanelSub>
            </div>
          </PanelHead>

          <DetailList>
            <DetailRow>
              <span><FaClock /> Date range</span>
              <b>{appliedFilters.fromDate} to {appliedFilters.toDate}</b>
            </DetailRow>
            <DetailRow>
              <span><FaShieldAlt /> Web access</span>
              <b>{getLabel(appliedFilters.webAccess, WEB_ACCESS_LABELS)}</b>
            </DetailRow>
            <DetailRow>
              <span><FaLayerGroup /> Plan</span>
              <b>{getLabel(appliedFilters.planStatus, PLAN_STATUS_LABELS)}</b>
            </DetailRow>
            <DetailRow>
              <span><FaChartLine /> Sorted by</span>
              <b>{getLabel(sortBy, SORT_LABELS)} • {getLabel(sortDir, SORT_DIRECTION_LABELS)}</b>
            </DetailRow>
          </DetailList>
        </Panel>
      </SplitGrid>

      <TablePanel>
        <PanelHead>
          <div>
            <PanelTitle>Restaurant List</PanelTitle>
            <PanelSub>Sales and order performance for each restaurant.</PanelSub>
          </div>
          <PanelMeta style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <button
              type="button"
              style={{ background: "linear-gradient(135deg, var(--app-primary), var(--app-primary-hover))", border: "none", color: "#fff", padding: "6px 12px", borderRadius: "8px", fontWeight: "700", cursor: "pointer", fontSize: "12px" }}
              onClick={exportExcel}
              disabled={!data.rows.length}
            >
              Export Excel
            </button>
            <button
              type="button"
              style={{ background: "linear-gradient(135deg, #0f766e, #0d9488)", border: "none", color: "#fff", padding: "6px 12px", borderRadius: "8px", fontWeight: "700", cursor: "pointer", fontSize: "12px" }}
              onClick={() => printThermal(reportRef.current, `restaurants_overview_${appliedFilters.fromDate}_to_${appliedFilters.toDate}`)}
              disabled={!data.rows.length}
            >
              <FaPrint /> Thermal Print
            </button><button
              type="button"
              style={{ background: "linear-gradient(135deg, #0f766e, #0d9488)", border: "none", color: "#fff", padding: "6px 12px", borderRadius: "8px", fontWeight: "700", cursor: "pointer", fontSize: "12px" }}
              onClick={() => printThermalViaUsb(reportRef.current, `restaurants_overview_${appliedFilters.fromDate}_to_${appliedFilters.toDate}`)}
              disabled={!data.rows.length}
            >
              <FaPrint /> Thermal Print via USB
            </button>
            <span>{refreshing ? "Refreshing..." : `${numberFormatter.format(data.pagination.total || 0)} total`}</span>
          </PanelMeta>
        </PanelHead>

        {loading ? (
          <CenterState>Loading overview table...</CenterState>
        ) : data.rows.length === 0 ? (
          <CenterState>No restaurants matched these filters.</CenterState>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>
                    <SortButton type="button" onClick={() => handleSort("restaurant_name")}>
                      Restaurant
                    </SortButton>
                  </th>
                  <th>Owner</th>
                  <th>Mobile</th>
                  <th>Plan</th>
                  <th>Web Access</th>
                  <th>
                    <SortButton type="button" onClick={() => handleSort("total_orders")}>
                      Orders
                    </SortButton>
                  </th>
                  <th>
                    <SortButton type="button" onClick={() => handleSort("total_sales")}>
                      Sales
                    </SortButton>
                  </th>
                  <th>
                    <SortButton type="button" onClick={() => handleSort("avg_order_value")}>
                      Avg. Bill Value
                    </SortButton>
                  </th>
                  <th>
                    <SortButton type="button" onClick={() => handleSort("last_order_at")}>
                      Last Order
                    </SortButton>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, index) => (
                  <tr key={row.restaurant_id}>
                    <td>{(page - 1) * perPage + index + 1}</td>
                    <td>
                      <TableTitle>{row.restaurant_name}</TableTitle>
                    </td>
                    <td>{row.owner_name || "-"}</td>
                    <td>{row.mobile_number || "-"}</td>
                    <td>
                      <StatusBadge $tone={row.plan_status}>
                        {getLabel(row.plan_status, PLAN_STATUS_LABELS)}
                        {row.package_type ? ` • ${getLabel(row.package_type, PACKAGE_LABELS)}` : ""}
                      </StatusBadge>
                    </td>
                    <td>
                      <StatusBadge $tone={row.web_access === "allowed" ? "allowed" : "blocked"}>
                        {row.web_access === "allowed" ? "Allowed" : "Blocked"}
                      </StatusBadge>
                    </td>
                    <td>{numberFormatter.format(row.total_orders || 0)}</td>
                    <td>{currencyFormatter.format(row.total_sales || 0)}</td>
                    <td>{currencyFormatter.format(row.avg_order_value || 0)}</td>
                    <td>{formatDateTime(row.last_order_at)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}

        <PaginationRow>
          <PaginationText>
            Page <b>{data.pagination.page || 1}</b> of <b>{totalPages || 1}</b>
          </PaginationText>
          <PaginationActions>
            <GhostButton
              type="button"
              disabled={page <= 1 || loading || refreshing}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              Previous
            </GhostButton>
            <GhostButton
              type="button"
              disabled={loading || refreshing || page >= Math.max(totalPages, 1)}
              onClick={() =>
                setPage((current) =>
                  totalPages ? Math.min(totalPages, current + 1) : current + 1
                )
              }
            >
              Next
            </GhostButton>
          </PaginationActions>
        </PaginationRow>
      </TablePanel>
    </Page>
  );
};

export default RestaurantsOverviewPage;

const Page = styled.div`
  min-height: 100%;
  padding: clamp(12px, 2vw, 18px);
  background:
    radial-gradient(circle at 10% 10%, rgba(208, 235, 255, 0.9), transparent 30%),
    radial-gradient(circle at 90% 0%, rgba(219, 234, 254, 0.85), transparent 34%),
    linear-gradient(180deg, #f4f8fc 0%, #eef4fb 100%);
  display: grid;
  gap: 16px;
  align-content: start;
`;

const Hero = styled.section`
  border-radius: 24px;
  padding: clamp(18px, 2.6vw, 26px);
  background: linear-gradient(135deg, #0b3b62 0%, var(--app-primary) 52%, var(--app-primary-hover) 100%);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 20px 42px rgba(15, 92, 149, 0.22);
  display: grid;
  gap: 18px;
`;

const HeroCopy = styled.div`
  display: grid;
  gap: 8px;
`;

const HeroBadge = styled.span`
  width: fit-content;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border: 1px solid rgba(255, 255, 255, 0.24);
  background: rgba(255, 255, 255, 0.14);
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-size: clamp(1.35rem, 3vw, 2rem);
  font-weight: 900;
`;

const HeroText = styled.p`
  margin: 0;
  max-width: 780px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
`;

const HeroMeta = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
`;

const HeroStat = styled.div`
  border-radius: 16px;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  display: grid;
  gap: 4px;

  span {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.8);
  }

  strong {
    font-size: 14px;
    font-weight: 800;
  }
`;

const FilterCard = styled.section`
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(148, 163, 184, 0.22);
  box-shadow: 0 16px 38px rgba(15, 23, 42, 0.06);
  padding: clamp(14px, 2vw, 18px);
  display: grid;
  gap: 14px;
`;

const FilterTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  font-weight: 800;
  color: #0f172a;
`;

const FilterActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
`;

const Field = styled.div`
  display: grid;
  gap: 6px;

  label {
    font-size: 12px;
    font-weight: 700;
    color: #475569;
  }

  input,
  select {
    width: 100%;
    min-height: 44px;
    border-radius: 12px;
    border: 1px solid #dbe5f0;
    background: #f8fbff;
    padding: 0 12px;
    font-size: 14px;
    color: #0f172a;
    outline: none;
  }

  input:focus,
  select:focus {
    border-color: var(--app-primary);
    box-shadow: 0 0 0 3px rgba(15, 92, 149, 0.12);
  }
`;

const InputWrap = styled.div`
  min-height: 44px;
  border-radius: 12px;
  border: 1px solid #dbe5f0;
  background: #f8fbff;
  padding: 0 12px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #64748b;
  }

  input {
    border: 0;
    padding: 0;
    min-height: auto;
    background: transparent;
  }

  input:focus {
    box-shadow: none;
  }
`;

const FooterRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const AppliedCopy = styled.div`
  font-size: 13px;
  color: #475569;
`;

const PrimaryButton = styled.button`
  border: none;
  border-radius: 12px;
  padding: 10px 14px;
  background: linear-gradient(135deg, var(--app-primary-dark), var(--app-primary));
  color: #fff;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  box-shadow: 0 12px 24px rgba(15, 92, 149, 0.22);

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
    box-shadow: none;
  }
`;

const GhostButton = styled.button`
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 10px 14px;
  background: #fff;
  color: #0f172a;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
`;

const SummaryCard = styled.div`
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.97);
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.06);
  padding: 16px;
  display: grid;
  gap: 8px;
`;

const SummaryIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  color: ${(props) => props.$accent || "var(--app-primary)"};
  background: ${(props) => `${props.$accent || "var(--app-primary)"}14`};
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const SummaryValue = styled.div`
  font-size: clamp(1.2rem, 2.6vw, 1.7rem);
  font-weight: 900;
  color: #0f172a;
`;

const SummaryHelper = styled.div`
  font-size: 13px;
  color: #64748b;
`;

const SplitGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(280px, 1fr);
  gap: 12px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.section`
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.97);
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.06);
  padding: 16px;
  display: grid;
  gap: 14px;
`;

const TablePanel = styled(Panel)`
  gap: 16px;
`;

const PanelHead = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-size: 1.02rem;
  color: #0f172a;
`;

const PanelSub = styled.p`
  margin: 4px 0 0;
  color: #64748b;
  font-size: 13px;
`;

const PanelMeta = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: var(--app-primary);
  background: var(--app-primary-soft);
  border: 1px solid var(--app-primary-soft);
  padding: 7px 10px;
  border-radius: 999px;
`;

const TopList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
`;

const TopCard = styled.div`
  border-radius: 18px;
  padding: 14px;
  background:
    linear-gradient(180deg, rgba(239, 246, 255, 0.95), rgba(255, 255, 255, 0.98));
  border: 1px solid var(--app-primary-soft);
  display: grid;
  gap: 6px;
`;

const TopRank = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: var(--app-primary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const TopName = styled.div`
  font-size: 15px;
  font-weight: 900;
  color: #0f172a;
`;

const TopMeta = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const TopStats = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
`;

const DetailList = styled.div`
  display: grid;
  gap: 10px;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #edf2f7;

  &:last-child {
    border-bottom: 0;
    padding-bottom: 0;
  }

  span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #475569;
    font-size: 13px;
  }

  b {
    color: #0f172a;
    font-size: 13px;
    text-align: right;
  }
`;

const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
`;

const Table = styled.table`
  width: 100%;
  min-width: 1080px;
  border-collapse: collapse;

  thead {
    background: #f8fbff;
  }

  th,
  td {
    padding: 12px 14px;
    border-bottom: 1px solid #edf2f7;
    text-align: left;
    vertical-align: top;
    font-size: 14px;
    color: #0f172a;
  }

  th {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #64748b;
  }

  tbody tr:hover {
    background: #fbfdff;
  }
`;

const SortButton = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  font: inherit;
  color: inherit;
  font-weight: inherit;
  text-transform: inherit;
  letter-spacing: inherit;
  cursor: pointer;
`;

const TableTitle = styled.div`
  font-weight: 800;
  color: #0f172a;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  border: 1px solid;

  ${(props) => {
    switch (props.$tone) {
      case "paid":
      case "allowed":
        return `
          color: #166534;
          background: #dcfce7;
          border-color: #bbf7d0;
        `;
      case "trial":
        return `
          color: var(--app-primary);
          background: var(--app-primary-soft);
          border-color: var(--app-primary-soft);
        `;
      case "expired":
      case "blocked":
        return `
          color: #b91c1c;
          background: #fee2e2;
          border-color: #fecaca;
        `;
      default:
        return `
          color: #475569;
          background: #f1f5f9;
          border-color: #e2e8f0;
        `;
    }
  }}
`;

const PaginationRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const PaginationText = styled.div`
  font-size: 13px;
  color: #475569;
`;

const PaginationActions = styled.div`
  display: flex;
  gap: 10px;
`;

const CenterState = styled.div`
  min-height: 180px;
  display: grid;
  place-items: center;
  text-align: center;
  color: #64748b;
  font-weight: 600;
`;

const NoticeCard = styled.section`
  border-radius: 18px;
  padding: 18px;
  background: ${(props) => (props.$danger ? "#fef2f2" : "#ffffff")};
  border: 1px solid ${(props) => (props.$danger ? "#fecaca" : "#e2e8f0")};
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.06);
`;

const NoticeTitle = styled.h2`
  margin: 0;
  color: #0f172a;
  font-size: 1rem;
`;

const NoticeText = styled.p`
  margin: 8px 0 0;
  color: #475569;
  line-height: 1.6;
`;
